import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { linkChiama } from '../../utils/percorso';
import { useAuth } from '../../context/AuthContext';
import Icona from '../../components/cliente/Icone';
import { Iniziale } from '../../components/staff/Persone';
import '../../components/staff/scheda.css';
import '../../components/staff/contatti.css';

// /admin/contatti: le persone che conosciamo ma che non sono (ancora) clienti (backend/contatti.py).
// In cima i cinque riquadri che dicono cosa fare oggi (aperti, da sentire, in ritardo, senza prossimo passo,
// fermi): si cliccano e filtrano davvero. Poi i chip per stato, le tendine fonte/segue, la ricerca e
// l'ordinamento — tutto scritto nell'indirizzo, così una lista si può mandare a qualcuno com'è.
// Ogni riga: iniziale colorata per fonte, nome, telefono da chiamare, fonte, cosa cerca, stato, chi segue e
// **il prossimo passo con la data** (rosso se è passata). "+ Nuovo contatto" è un modulo da venti secondi.

export const FILTRI = [
  ['aperti', 'aperti', ''],
  ['da_sentire', 'da sentire', 'hot'],
  ['in_ritardo', 'in ritardo', 'bad'],
  ['senza_prossimo_passo', 'senza prossimo passo', 'hot'],
  ['fermi', 'fermi da una settimana', 'bad'],
];

const ORDINI = [['prossimo_passo', 'Prossimo passo'], ['recenti', 'Arrivati per ultimi'], ['nome', 'Nome']];

// Gli stati chiusi: in lista e nella scheda si vedono in verde (diventato cliente) o in rosso (perso)
export const classeStato = (stato) => (stato === 'diventato_cliente' ? 'ok' : stato === 'perso' ? 'no' : 'vivo');

export const inizialeDi = (c) => (c?.nome || c?.nome_intero || '?').trim().charAt(0).toUpperCase() || '?';

// Cosa cerca, in una riga: dove, che tipo, quanto. Se non l'abbiamo ancora chiesto, le due righe libere.
export const cosaCerca = (c) => [c?.cerca_comune, c?.cerca_tipo, c?.cerca_budget].filter(Boolean).join(' · ') || c?.cerca_note || '';

// Le date arrivano dal server in ISO: qui si leggono come le legge una persona
export const giorno = (iso, conAnno = false) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const oggi = new Date();
  if (d.toDateString() === oggi.toDateString()) return 'oggi';
  return `${d.getDate()} ${d.toLocaleDateString('it-IT', { month: 'short' })}${conAnno ? ` ${d.getFullYear()}` : ''}`;
};

// Quanto tiene il server, campo per campo (backend/contatti.py): la casella si ferma dove si ferma lui
export const MAX_TESTO = 500;
export const MAX_NOME = 80;
export const MAX_CAMPO = { nome: MAX_NOME, cognome: MAX_NOME };
export const quanto = (k) => MAX_CAMPO[k] || MAX_TESTO;

// La data per <input type="date"> (che vuole per forza aaaa-mm-gg)
export const aInput = (iso) => (iso ? String(iso).slice(0, 10) : '');

export const erroreDi = (e, ripiego = 'Non riuscito') => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : ripiego;
};

// Il modulo corto: nome, telefono, fonte, cosa cerca. Con «passaparola» compare anche chi ce l'ha mandato.
// Se quel numero lo conosciamo già il server risponde 409 col contatto che c'è: si mostra il link e, se si è
// sicuri, «Aggiungilo lo stesso».
export const NuovoContatto = ({ fonti, chiudi, onCreato }) => {
  const [dati, setDati] = useState({ nome: '', telefono: '', fonte: 'instagram', fonte_nome: '', cerca_note: '' });
  const [attesa, setAttesa] = useState(false);
  const [doppione, setDoppione] = useState(null);
  // La casella si ferma da sola dove si ferma il server (backend/contatti.py: MAX_NOME, MAX_TESTO): prima si
  // poteva scrivere un nome lunghissimo, il server ne teneva 80 e il resto spariva senza dirlo (blocco 3).
  const campo = (k) => ({
    value: dati[k],
    ...(k === 'fonte' ? {} : { maxLength: MAX_CAMPO[k] || MAX_TESTO }),   // la tendina non è una casella di testo
    onChange: (e) => setDati({ ...dati, [k]: e.target.value }),
  });

  const manda = async (ancheSeEsiste) => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/contatti`, { ...dati, anche_se_esiste: ancheSeEsiste || undefined });
      toast.success(`${r.data.contatto?.nome_intero || 'Contatto'} aggiunto`);
      setDoppione(null);
      onCreato(r.data.contatto);
      chiudi();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === 'object') setDoppione(d);
      else toast.error(erroreDi(e));
    } finally { setAttesa(false); }
  };

  return (
    <div className="card hero" style={{ marginBottom: '1rem' }} data-testid="nuovo-contatto">
      <div className="ttl"><div className="i"><Icona nome="persone" /></div><h2>Nuovo contatto</h2>
        <button type="button" className="link" onClick={chiudi}>Chiudi</button></div>
      {doppione && (
        <div className="doppione" data-testid="avviso-doppione">
          <b>{doppione.messaggio}</b>
          <span>{[doppione.contatto?.telefono, doppione.contatto?.email, doppione.contatto?.stato_testo, doppione.contatto?.fonte_testo].filter(Boolean).join(' · ')}</span>
          <div className="azioni">
            <Link className="btn sm" to={`/admin/contatti/${doppione.contatto?.id}`}>Apri {doppione.contatto?.nome_intero}</Link>
            <button type="button" className="btn sm" onClick={() => manda(true)} disabled={attesa}>Aggiungilo lo stesso</button>
          </div>
        </div>
      )}
      <form className="form-griglia" onSubmit={(e) => { e.preventDefault(); manda(false); }} style={{ marginTop: 0 }}>
        <label>Nome<input {...campo('nome')} placeholder="Come si chiama" required /></label>
        <label>Telefono<input {...campo('telefono')} placeholder="es. 340 1234567" inputMode="tel" /></label>
        <label>Come ci ha conosciuto
          <select {...campo('fonte')}>{Object.entries(fonti || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </label>
        {dati.fonte === 'passaparola' && <label>Chi ce l&apos;ha mandato<input {...campo('fonte_nome')} placeholder="Nome di chi ce l'ha mandato" /></label>}
        <label>Cosa cerca<input {...campo('cerca_note')} placeholder="es. trilocale a Monza, 200mila" /></label>
        <div style={{ alignSelf: 'end', display: 'flex', gap: '.4rem' }}>
          <button type="submit" className="btn sm p" disabled={attesa}>{attesa ? 'Salvo…' : 'Aggiungi'}</button>
        </div>
      </form>
    </div>
  );
};

const Riga = ({ c, admin, onElimina }) => {
  const navigate = useNavigate();
  const apri = () => navigate(`/admin/contatti/${c.id}`);
  const tel = linkChiama(c.telefono);
  const cerca = cosaCerca(c);
  return (
    <div className="tr ct" data-testid={`contatto-${c.id}`} role="link" tabIndex={0}
      onClick={apri} onKeyDown={(e) => { if (e.key === 'Enter') apri(); }}>
      <div className="nm">
        <div className={`av f-${c.fonte || 'altro'}`} aria-hidden="true">{inizialeDi(c)}</div>
        <div style={{ minWidth: 0 }}>
          <b>{c.nome_intero}</b>
          <span>{c.fonte_nome ? `da ${c.fonte_nome}` : c.email || '—'}</span>
          {/* sul telefono le altre colonne spariscono: il prossimo passo e lo stato restano qui, perché sono
              la ragione per cui si guarda questa lista */}
          <span className={`solo-telefono${c.in_ritardo ? ' tardi' : ''}`} data-testid={`passo-piccolo-${c.id}`}>
            {c.stato_testo}
            {c.prossimo_passo ? ` · ${c.prossimo_passo}` : ' · nessun prossimo passo'}
            {c.prossimo_passo_il ? ` · ${c.in_ritardo ? 'era ' : ''}${giorno(c.prossimo_passo_il)}` : ''}
          </span>
        </div>
      </div>
      <div>{tel
        ? <a className="tel" href={tel} onClick={(e) => e.stopPropagation()}>{c.telefono}</a>
        : <span className="muto">—</span>}</div>
      <div className="f-col"><span className="fnt"><i className={`f-${c.fonte || 'altro'}`} /><b>{c.fonte_testo}</b></span></div>
      <div className="cerca">{cerca || <span className="muto">—</span>}</div>
      <div><span className={`st-pill ${classeStato(c.stato)}`}>{c.stato_testo}</span></div>
      <div className="cs">{c.segue ? <><Iniziale persona={c.segue} taglia={22} />{c.segue.nome}</> : <span className="muto">—</span>}</div>
      <div className={`nx${c.in_ritardo ? ' tardi' : ''}`} data-testid={`passo-${c.id}`}>
        {c.prossimo_passo || <span className="muto">nessun prossimo passo</span>}
        {c.prossimo_passo_il && <span>{c.in_ritardo ? 'era ' : ''}{giorno(c.prossimo_passo_il)}</span>}
      </div>
      <div className="az">
        <Link className="btn sm" to={`/admin/contatti/${c.id}`} onClick={(e) => e.stopPropagation()}>Apri</Link>
        {admin && (
          <button type="button" className="btn sm rosso-vuoto ico-sola" title="Elimina il contatto"
            aria-label={`Elimina ${c.nome_intero}`} onClick={(e) => { e.stopPropagation(); onElimina(c); }}>
            <Icona nome="cestino" />
          </button>
        )}
      </div>
    </div>
  );
};

const Contatti = () => {
  const { userType } = useAuth();
  const admin = userType === 'admin';
  const [params, setParams] = useSearchParams();
  const [dati, setDati] = useState(null);
  const [nuovo, setNuovo] = useState(false);
  const [giro, setGiro] = useState(0);
  const [elimina, setElimina] = useState(null);

  const stato = params.get('stato') || '';
  const fonte = params.get('fonte') || '';
  const segue = params.get('segue') || '';
  const filtro = params.get('filtro') || '';
  const ordina = params.get('ordina') || 'prossimo_passo';
  const q = params.get('q') || '';
  const [cerca, setCerca] = useState(q);

  const imposta = (k, v) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set(k, v); else n.delete(k);
    return n;
  }, { replace: true });
  // un riquadro si accende e si spegne: è il modo più corto per dire "fammi vedere solo questi"
  const cambiaFiltro = (v) => imposta('filtro', filtro === v ? '' : v);

  // la ricerca aspetta 200 ms prima di finire nell'indirizzo (e quindi di ripartire con la chiamata)
  useEffect(() => {
    if (cerca === q) return undefined;
    const t = setTimeout(() => imposta('q', cerca), 200);
    return () => clearTimeout(t);
  }, [cerca]); // eslint-disable-line

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/contatti`, { params: { stato: stato || undefined, fonte: fonte || undefined, consulente: segue || undefined, filtro: filtro || undefined, q: q || undefined, ordina } })
      .then((r) => { if (vivo) setDati(r.data); })
      .catch(() => { if (vivo) setDati(false); });
    return () => { vivo = false; };
  }, [stato, fonte, segue, filtro, ordina, q, giro]);

  const conti = dati?.conti || {};
  const stati = dati?.stati || {};
  const fonti = dati?.fonti || {};
  const persone = dati?.persone || [];
  const righe = useMemo(() => dati?.contatti || [], [dati]);

  const confermaElimina = async () => {
    try {
      await axios.delete(`${API}/contatti/${elimina.id}`);
      toast.success(`${elimina.nome_intero}: eliminato`);
      setElimina(null);
      setGiro((g) => g + 1);
    } catch (e) { toast.error(erroreDi(e, 'Non eliminato')); }
  };

  if (dati === false) return <main className="st wrap"><p className="vuoto">Non riesco a caricare i contatti.</p></main>;

  return (
    <main className="st wrap" data-testid="contatti">
      <div className="head r">
        <div><div className="k">Contatti</div>
          <h1>{conti.totale ?? '…'} contatti, <em>{conti.aperti ?? 0} ancora aperti</em></h1>
          <p>Le persone che conosciamo ma che non sono (ancora) clienti: da dove arrivano, cosa cercano e qual è il prossimo passo, con la data.</p></div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn p" onClick={() => setNuovo(true)}>+ Nuovo contatto</button>
        </div>
      </div>

      <div className="nums n5 r">
        {FILTRI.map(([k, nome, classe]) => (
          <button type="button" key={k} className={`num ${classe}${filtro === k ? ' on' : ''}`} onClick={() => cambiaFiltro(k)} data-testid={`riquadro-${k}`}>
            <b>{conti[k] ?? 0}</b><span>{nome}</span>
          </button>
        ))}
      </div>

      {nuovo && <NuovoContatto fonti={fonti} chiudi={() => setNuovo(false)} onCreato={() => setGiro((g) => g + 1)} />}

      {elimina && (
        <div className="conferma-elimina" role="alertdialog" aria-label="Eliminare il contatto" data-testid="conferma-elimina">
          <p>Eliminare <b>{elimina.nome_intero}</b>? Se ne va anche la sua storia, e non si può annullare.</p>
          <button type="button" className="btn sm" onClick={() => setElimina(null)}>Annulla</button>
          <button type="button" className="btn sm rosso-vuoto" onClick={confermaElimina}>Elimina</button>
        </div>
      )}

      <div className="tools r">
        <button type="button" className={`chip${stato ? '' : ' on'}`} onClick={() => imposta('stato', '')}>Tutti</button>
        {Object.entries(stati).map(([k, v]) => (
          <button type="button" key={k} className={`chip${stato === k ? ' on' : ''}`} onClick={() => imposta('stato', stato === k ? '' : k)}>{v}</button>
        ))}
        <span className="sp" />
        <label className="srch"><Icona nome="cerca" />
          <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca nome, telefono, email…" aria-label="Cerca contatto" />
        </label>
      </div>

      <div className="tools r" style={{ gap: '.4rem' }}>
        <span className={`chip${fonte ? ' on' : ''}`}>
          <select value={fonte} onChange={(e) => imposta('fonte', e.target.value)} aria-label="Fonte">
            <option value="">Come ci ha conosciuto ▾</option>
            {Object.entries(fonti).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </span>
        {persone.length > 1 && (
          <span className={`chip${segue ? ' on' : ''}`}>
            <select value={segue} onChange={(e) => imposta('segue', e.target.value)} aria-label="Chi segue">
              <option value="">Segue: tutti ▾</option>
              {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </span>
        )}
        <span className="sp" />
        <span className="chip">
          <select value={ordina} onChange={(e) => imposta('ordina', e.target.value === 'prossimo_passo' ? '' : e.target.value)} aria-label="Ordina">
            {ORDINI.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </span>
      </div>

      <div className="tbl r">
        <div className="tr ct testa" style={{ cursor: 'default' }}>
          <div>Contatto</div><div>Telefono</div><div className="f-col">Fonte</div><div>Cosa cerca</div><div>Stato</div><div>Segue</div><div>Prossimo passo</div><div />
        </div>
        {!dati && <div className="tr tutti"><span className="muto">Carico i contatti…</span></div>}
        {dati && !righe.length && <div className="tr tutti"><span className="muto">Nessun contatto {q ? `per “${q}”` : 'con questi filtri'}.</span></div>}
        {righe.map((c) => <Riga key={c.id} c={c} admin={admin} onElimina={setElimina} />)}
      </div>
    </main>
  );
};

export default Contatti;
